import React from "react";

/**
 * The Broadcasts card surface.
 *
 * 10px radius, a 1px hairline border, white fill — and NO shadow. The spec
 * gives the whole console exactly one lift (the push-preview card) and depth
 * everywhere else comes from hairlines and the warm accent. Adding a shadow
 * here would quietly undo that.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Card contents.
 * @param {string} [props.className] - Extra classes (padding is the caller's).
 * @param {React.ElementType} [props.as='div'] - Element to render, e.g. 'section'.
 */
export const Card = ({ children, className = "", as: Tag = "div" }) => (
  <Tag className={`rounded-[10px] border border-bc-border bg-white ${className}`}>
    {children}
  </Tag>
);

/**
 * A card's title row: title, optional subtitle beneath, optional actions right.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.title - The heading text.
 * @param {React.ReactNode} [props.subtitle] - Secondary line under the title.
 * @param {React.ReactNode} [props.actions] - Node pinned to the right edge.
 * @param {string} [props.className] - Extra classes.
 */
export const CardHeader = ({ title, subtitle, actions, className = "" }) => (
  <div className={`flex items-start justify-between gap-3 ${className}`}>
    <div className="min-w-0">
      <div className="text-base font-semibold text-bc-text">{title}</div>
      {subtitle ? <div className="mt-0.5 text-xs text-bc-soft">{subtitle}</div> : null}
    </div>
    {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
  </div>
);

/**
 * The uppercase micro-label that sits above every field and metric.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Label text.
 * @param {string} [props.className] - Extra classes.
 */
export const CardLabel = ({ children, className = "" }) => (
  <div
    className={`text-[11px] uppercase tracking-[0.06em] text-bc-faint font-semibold ${className}`}
  >
    {children}
  </div>
);
